import React, { CSSProperties, useMemo, useRef } from "react"
import { useTranslation } from "react-i18next"

import { useEvent } from "shared/lib/useEvent"
import { useScreenSize } from "shared/lib/useScreenSize"
import { useAccessibilityMessage } from "shared/types/useAccessibilityMessage"

import { formatProjectPosition } from "entities/projects/lib/formatProjectPosition"
import { useProductPositionTableRowPayload } from "widgets/product-position-table/contexts/ProductPositionTableRowPayloadContext"
import { getProductPositionTableProductPositionColumnSizes } from "widgets/product-position-table/getProductPositionTableColumnSizes"

import { ProductPositionRowMenuCellHandle } from "../../cells/ProductPositionRowMenuCell"
import { ProductPositionTableProductPositionRowProps } from "../ProductPositionTableProductPositionRow"
import { ProductPositionTableProductPositionRowViewModel } from "../ProductPositionTableProductPositionRowView"

export function useProductPositionTableProductPositionRowViewModel(
  props: ProductPositionTableProductPositionRowProps
): ProductPositionTableProductPositionRowViewModel {
  const { t } = useTranslation()
  const { isMobile, isTablet } = useScreenSize()
  const {
    item,
    level,
    parentIndices,
    rowElementProps,
    isFocused,
    onCollapse,
    title,
    productType,
    isPending: isPendingProp,
    actions = []
  } = props
  const {
    hideQuantity,
    showClarificationRequestColumn,
    showSelectionCheckbox,
    columnPreset
  } = useProductPositionTableRowPayload()

  const rowRef = useRef<HTMLDivElement | null>(null)
  const menuCellRef = useRef<ProductPositionRowMenuCellHandle>(null)

  const style: CSSProperties = useMemo(
    () => ({
      ...rowElementProps?.style,
      ...getProductPositionTableProductPositionColumnSizes({
        level,
        hideVariantKey: isTablet,
        hideQuantity,
        showClarificationRequestColumn,
        hideMenu: isTablet,
        showSelectionCheckbox,
        columnPreset
      })
    }),
    [
      hideQuantity,
      isTablet,
      level,
      rowElementProps?.style,
      showClarificationRequestColumn,
      showSelectionCheckbox,
      columnPreset
    ]
  )

  const displayTitle = isMobile ? productType : title || productType

  const position = useMemo(
    () => formatProjectPosition(parentIndices, item.isAlternative),
    [parentIndices, item.isAlternative]
  )

  const handleCollapse = useEvent((e: React.MouseEvent) => {
    e.stopPropagation()
    onCollapse()
  })

  const isPending = !!isPendingProp

  const handleKeyDown = useEvent((e: React.KeyboardEvent<HTMLDivElement>) => {
    rowElementProps?.onKeyDown?.(e)
    if (
      actions.length > 0 &&
      ((e.key === "F10" && e.shiftKey) || (e.key === "ArrowDown" && e.altKey))
    ) {
      e.preventDefault()
      menuCellRef.current?.openMenu()
    }
  })

  useAccessibilityMessage(
    {
      id: "focused-row-room-info",
      text: t("ProjectPage.Accessibility.ProductRowFocus", {
        title,
        position,
        configuration: item.variantKey
      })
    },
    isFocused
  )

  const hasChildren = !!item.addons && item.addons.length > 0

  return {
    ...props,
    isPending,
    displayTitle,
    position,
    variantKey: item.variantKey,
    style,
    hasChildren,
    showClarificationRequestColumn,
    onCollapse: handleCollapse,
    actions,
    rowRef,
    menuCellRef,
    rowElementProps: rowElementProps
      ? {
          ...rowElementProps,
          onKeyDown: handleKeyDown
        }
      : rowElementProps
  }
}
